import { useMemo } from 'react';

import { Button, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

export type ConfirmModalData = {
	/**
	 * - horizontal: aligns to the right, "cancel" first, then "confirm", width adapted to content
	 * - horizontal-wide: takes full width, "cancel" first, then "confirm", each 50% width
	 *
	 * defaults to "horizontal"
	 */
	buttonsOrientation?: 'horizontal' | 'horizontal-wide';
	/**
	 * defaults to fe.components.confirm_modal.buttons.cancel
	 */
	cancelLabel?: string;
	cancelDataQa?: string;
	/**
	 * defaults to fe.components.confirm_modal.buttons.confirm
	 */
	confirmLabel?: string;
	confirmDataQa?: string;
	leadingSlot?: React.ReactNode;
	subTitle?: string;
	text?: React.ReactNode | readonly React.ReactNode[];
	title?: string;
	buttonSize?: 'large' | 'small' | 'x-small';
};

export type ConfirmModalContentProps = ConfirmModalData & {
	/**
	 * Called when the confirm (primary) button is pressed.
	 * The DS `Modal` closes itself when a footer button is pressed.
	 */
	onAccept: () => void;
	/**
	 * Called when the cancel (secondary) button is pressed.
	 * The DS `Modal` closes itself when a footer button is pressed.
	 */
	onReject: () => void;
};

function isTextArray(text: React.ReactNode | readonly React.ReactNode[]): text is readonly React.ReactNode[] {
	return Array.isArray(text);
}

/**
 * The header/body/footer markup shared by the trigger-based `ConfirmModal` component and the
 * imperative `useConfirm` hook. Must be rendered as the children of a DS `<Modal>`.
 */
export function ConfirmModalContent({
	buttonsOrientation = 'horizontal',
	cancelDataQa,
	cancelLabel,
	confirmDataQa,
	confirmLabel,
	leadingSlot,
	onAccept,
	onReject,
	subTitle,
	text,
	title,
	buttonSize,
}: ConfirmModalContentProps): JSX.Element {
	const { translate } = useI18n();

	const textArray = useMemo(() => {
		if (!text) return [];
		return isTextArray(text) ? text : [text];
	}, [text]);

	const buttons = useMemo(() => {
		const list = [
			{
				type: 'accept',
				variant: 'primary' as const,
				choice: true,
				label: confirmLabel || translate('fe.components.confirm_modal.buttons.confirm'),
				qa: confirmDataQa,
			},
			{
				type: 'reject',
				variant: 'secondary' as const,
				choice: false,
				label: cancelLabel || translate('fe.components.confirm_modal.buttons.cancel'),
				qa: cancelDataQa,
			},
		];
		return list;
	}, [translate, cancelDataQa, confirmDataQa, cancelLabel, confirmLabel]);

	return (
		<>
			<ModalHeader title={title} subtitle={subTitle} leadingSlot={leadingSlot} />

			{textArray.length > 0 && (
				<ModalBody>
					{textArray.map((t, idx) => (
						<Text className="mt-4" key={idx} as="p">
							{t}
						</Text>
					))}
				</ModalBody>
			)}

			<ModalFooter>
				{({ close }) =>
					buttons.map(({ choice, label, qa, variant, type }) => (
						<Button
							className={cn({
								'w-full md:w-[calc(50%)]': buttonsOrientation === 'horizontal-wide',
								'w-full md:w-auto': buttonsOrientation === 'horizontal',
							})}
							key={choice.toString()}
							variant={variant}
							size={buttonSize}
							data-qa={qa}
							onClick={() => {
								type === 'accept' ? onAccept() : onReject();
								close();
							}}
						>
							<div className="text-wrap">{label}</div>
						</Button>
					))
				}
			</ModalFooter>
		</>
	);
}
